import { useState } from 'react';
import api from '../services/api';
import { GradeDial } from './RatingReportView';
import Markdown from './Markdown';
import ConfirmDialog from './ConfirmDialog';
import { Notice, Spinner, StatusPill } from './ui';
import { errorMessage } from '../lib/http';
import { SEVERITY, formatDateTime } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, CARD, CARD_STYLE } from '../lib/ui';
import type { RatingReport, RemediationItem } from '../types/workspace';

type Props = {
  companyId: string;
  companyName: string;
  report: RatingReport;
  items: RemediationItem[];
  onChanged: (message?: string) => void;
  onError: (message: string) => void;
};

type ConfirmKind = 'publish' | 'discard' | null;

function Fact({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-[#4a1a30]">{value || '—'}</p>
    </div>
  );
}

export default function DraftReportEditor({ companyId, companyName, report, items, onChanged, onError }: Props) {
  const [busy, setBusy] = useState<'publish' | 'discard' | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(true);
  const strengths = report.strengths || [];
  const risks = report.risks || [];

  const publish = async () => {
    setBusy('publish');
    setError(null);
    try {
      const { data } = await api.post<{ report: RatingReport; emailSent?: boolean }>(
        `/admin/companies/${companyId}/reports/${report.id}/publish`,
      );
      setConfirm(null);
      onChanged(
        data.emailSent === false
          ? `Rating v${report.version} published, but the customer email could not be sent — check API mail logs.`
          : `Rating v${report.version} published — customer emailed. Next, agree the Ready summary with the company.`,
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not publish.'));
      onError(errorMessage(err, 'Could not publish.'));
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    setBusy('discard');
    setError(null);
    try {
      await api.delete(`/admin/companies/${companyId}/reports/${report.id}`);
      setConfirm(null);
      onChanged('Draft discarded.');
    } catch (err) {
      setError(errorMessage(err));
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className={`${CARD} overflow-hidden`} style={{ ...CARD_STYLE, border: '1.5px dashed #f9c0d8' }}>
      <div className="flex flex-wrap items-center gap-4 p-5" style={{ background: 'linear-gradient(135deg,#f8fafc 0%,#ffffff 70%)' }}>
        <GradeDial grade={report.grade || '—'} score={report.score} size={96} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill label={`Draft v${report.version}`} tone="slate" />
            {report.outlook && (
              <StatusPill
                label={`${report.outlook} outlook`}
                tone={report.outlook.toLowerCase().startsWith('neg') ? 'rose' : 'green'}
              />
            )}
            <span className="text-[11px] text-[#c084a0]">
              Generated {formatDateTime(report.createdAt)}
              {report.model ? ` · ${report.model}` : ''}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-[#6b3a52]">
            AI draft — read-only in Consult. Publish to send it to the customer as their official rating.
          </p>
        </div>
      </div>

      <div className="grid gap-4 border-t border-[#fce7f3] p-5 sm:grid-cols-3">
        <Fact label="Grade" value={report.grade} />
        <Fact label="Score" value={`${report.score}/100`} />
        <Fact label="Outlook" value={report.outlook} />
      </div>

      <div className="border-t border-[#fce7f3] px-5 py-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Summary</p>
        <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{report.summary}</p>
      </div>

      {(strengths.length > 0 || risks.length > 0) && (
        <div className="grid gap-px bg-[#fce7f3] sm:grid-cols-2">
          <div className="bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#047857]">Strengths</p>
            <ul className="mt-2 space-y-1.5">
              {strengths.length === 0 && <li className="text-xs text-[#c084a0]">None listed.</li>}
              {strengths.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#4a1a30]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#be123c]">Key risks</p>
            <ul className="mt-2 space-y-1.5">
              {risks.length === 0 && <li className="text-xs text-[#c084a0]">None listed.</li>}
              {risks.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm text-[#4a1a30]">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {report.reportMarkdown && (
        <div className="border-t border-[#fce7f3]">
          <button
            type="button"
            onClick={() => setReportOpen((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#d6006e] hover:bg-[#fff5f8]"
          >
            Full rating report
            <span>{reportOpen ? '−' : '+'}</span>
          </button>
          {reportOpen && (
            <div className="px-5 pb-5">
              <Markdown source={report.reportMarkdown} />
            </div>
          )}
        </div>
      )}

      {report.analystNotes && (
        <div className="border-t border-[#fce7f3] px-5 py-4" style={{ background: '#fffafc' }}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Internal analyst notes</p>
          <p className="mt-1.5 whitespace-pre-wrap text-sm text-[#6b3a52]">{report.analystNotes}</p>
        </div>
      )}

      <div className="border-t border-[#fce7f3] p-5">
        <p className="text-sm font-bold text-[#6b0030]">Remediation items · {items.length}</p>
        <p className="text-[11px] text-[#c084a0]">Published with the report for the company to action.</p>
        <ul className="mt-2 space-y-2">
          {items.length === 0 && <li className="text-xs text-[#c084a0]">None.</li>}
          {items.map((i) => {
            const changeable = i.changeable !== false;
            const exact = (i.exactChange || (changeable ? i.detail : null) || '').trim();
            return (
              <li
                key={i.id}
                className="rounded-xl px-3 py-2"
                style={{
                  background: changeable ? '#fffafc' : '#fffbeb',
                  border: `1px solid ${changeable ? '#fce7f3' : '#fcd34d'}`,
                }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill label={SEVERITY[i.severity]?.label || i.severity} tone={SEVERITY[i.severity]?.tone || 'slate'} />
                  {!changeable && (
                    <StatusPill
                      label={`Cannot change · ${String(i.immutableKind || 'OTHER')}`}
                      tone="amber"
                    />
                  )}
                  <span className="text-sm font-semibold text-[#6b0030]">{i.title}</span>
                </div>
                {i.documentCode && <p className="mt-1 text-[11px] font-semibold text-[#d6006e]">Document · {i.documentTitle || i.documentCode}</p>}
                {changeable && exact && (
                  <p className="mt-1 text-xs text-[#6b3a52]">
                    <span className="font-bold text-[#d6006e]">{i.severity === 'HIGH' ? 'AI mitigation: ' : 'Exact change: '}</span>
                    {exact}
                  </p>
                )}
                {i.detail && (exact !== i.detail.trim() || !changeable) && <p className="mt-0.5 text-xs text-[#6b3a52]">{i.detail}</p>}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-[#fce7f3] p-5">
        {error && (
          <div className="mb-2 w-full">
            <Notice tone="rose">{error}</Notice>
          </div>
        )}
        <button type="button" disabled={busy !== null} onClick={() => setConfirm('publish')} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
          {busy === 'publish' && <Spinner />} Publish to customer
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => setConfirm('discard')}
          className="ml-auto rounded-xl px-3 py-2 text-xs font-bold text-[#be123c] hover:bg-[#fff1f2]"
        >
          Discard draft
        </button>
      </div>

      <ConfirmDialog
        open={confirm === 'publish'}
        title={`Publish rating v${report.version}?`}
        description={`Publish this AI rating to ${companyName}. The customer will be emailed and can view it once the rating fee is paid (or immediately if already paid).`}
        confirmLabel="Publish rating"
        cancelLabel="Keep as draft"
        busy={busy === 'publish'}
        onCancel={() => busy !== 'publish' && setConfirm(null)}
        onConfirm={() => void publish()}
      />
      <ConfirmDialog
        open={confirm === 'discard'}
        title="Discard this draft?"
        description="This removes the draft rating and its remediation items. You can generate a new draft afterward."
        confirmLabel="Discard draft"
        cancelLabel="Keep draft"
        tone="danger"
        busy={busy === 'discard'}
        onCancel={() => busy !== 'discard' && setConfirm(null)}
        onConfirm={() => void discard()}
      />
    </article>
  );
}
